import { FC, forwardRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { mdiClose } from "@mdi/js";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  FormControl,
  TextField,
} from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * MiAutocomplete - A customizable autocomplete component built on top of Material-UI Autocomplete
 *
 * This component provides a consistent autocomplete/search input interface with features including:
 * - Type-ahead search functionality
 * - Custom option rendering
 * - Validation and error handling
 * - Configurable focus behavior
 * - Custom clear functionality
 * - Accessibility support with test IDs
 * - Keyboard event handling
 *
 * @example
 * // Basic usage with simple options
 * <MiAutocomplete
 *   name="countries"
 *   label="Select Country"
 *   options={[
 *     { label: 'United States', value: 'US' },
 *     { label: 'Canada', value: 'CA' },
 *     { label: 'Mexico', value: 'MX' }
 *   ]}
 *   value={selectedCountry}
 *   onChange={(event, newValue) => setSelectedCountry(newValue)}
 * />
 *
 * @example
 * // With custom clear handler and validation
 * <MiAutocomplete
 *   name="users"
 *   label="Assign User"
 *   options={userOptions}
 *   value={assignedUser}
 *   handleClear={() => {
 *     setAssignedUser(null);
 *     console.log('User assignment cleared');
 *   }}
 *   error={errors.assignedUser}
 *   required
 *   onChange={handleUserChange}
 * />
 *
 * @example
 * // With custom render input and styling
 * <MiAutocomplete
 *   name="products"
 *   label="Search Products"
 *   options={productOptions}
 *   value={selectedProducts}
 *   multiple
 *   containerStyles={{ marginBottom: '16px' }}
 *   renderInput={(params) => (
 *     <TextField
 *       {...params}
 *       variant="outlined"
 *       placeholder="Type to search products..."
 *     />
 *   )}
 *   onChange={handleProductSelection}
 * />
 *
 * @example
 * // With focus behavior and additional TextField props
 * <MiAutocomplete
 *   name="tags"
 *   label="Tags"
 *   options={tagOptions}
 *   value={selectedTags}
 *   openOnFocus={false}
 *   textFieldOnFocus={() => console.log('Field focused')}
 *   textFieldProps={{
 *     variant: 'filled',
 *     helperText: 'Select relevant tags'
 *   }}
 *   freeSolo
 *   multiple
 *   onChange={handleTagChange}
 * />
 */
const MiAutocompleteInner: FC<MiAutocompleteProps> = ({
  openOnFocus = true,
  options = [],
  containerStyles,
  renderInput,
  value,
  handleClear = () => null,
  name = "",
  label,
  required,
  textFieldOnFocus,
  textFieldProps,
  error,
  renderOption,
  clearOnBlur,
  loading,
  noOptionsText,
  className,
  forcePopupIcon,
  PaperComponent,
  blurOnSelect = false,
  onOpen,
  ref,
  ...rest
}) => {
  const { classes } = useStyles({ containerStyles });

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
    onOpen?.();
  };

  const handleRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any
  ) => {
    if (renderOption) {
      return renderOption(props, option, handleClose);
    }

    return null;
  };

  return (
    <FormControl className={classes.container} fullWidth>
      <Autocomplete
        ref={ref}
        {...rest}
        slots={{ paper: PaperComponent }}
        data-testid={`mi-autocomplete-${name}`}
        openOnFocus={openOnFocus}
        options={options}
        size="small"
        value={value}
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        clearIcon={<MiIcon path={mdiClose} size={0.8} onClick={handleClear} />}
        renderInput={
          renderInput ||
          (({ inputProps, ...params }) => (
            <TextField
              {...params}
              {...textFieldProps}
              required={required}
              label={label}
              error={!!error}
              helperText={error ? error.message : ""}
              slotProps={{
                htmlInput: {
                  "data-testid": `mi-autocomplete-input-${name}`,
                  ...inputProps,
                  ...(typeof value === "string" || typeof value === "number"
                    ? { value: value ?? inputProps.value ?? "" }
                    : {}),
                },
              }}
              onFocus={textFieldOnFocus}
            />
          ))
        }
        renderOption={renderOption ? handleRenderOption : undefined}
        clearOnBlur={clearOnBlur}
        loading={loading}
        noOptionsText={noOptionsText}
        className={className}
        forcePopupIcon={forcePopupIcon}
        blurOnSelect={blurOnSelect}
      />
    </FormControl>
  );
};

export const MiAutocomplete = forwardRef<HTMLInputElement, MiAutocompleteProps>(
  (props, ref) => {
    return <MiAutocompleteInner {...props} ref={ref} />;
  }
);

const useStyles = makeStyles<{ containerStyles?: React.CSSProperties }>()(
  (_, { containerStyles }) => ({
    container: {
      boxSizing: "border-box",
      textAlign: "center",
      ...containerStyles,
    },
  })
);

interface MiAutocompleteProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "autoComplete" | "size" | "value" | "onChange"
  > {
  openOnFocus?: boolean;
  options?: Array<{
    value: string | number | undefined;
    label: string;
    [key: string]: any;
  }>;
  containerStyles?: React.CSSProperties;
  getOptionLabel?: (option: any) => string;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any,
    close: () => void
  ) => React.ReactNode;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  inputValue?: any;
  onChange?: (event: React.SyntheticEvent, value: any) => void;
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  value?: any;
  handleClear?: () => void;
  name?: string;
  label?: string;
  required?: boolean;
  textFieldOnFocus?: () => void;
  textFieldProps?: React.ComponentProps<typeof TextField>;
  error?: { message: string };
  clearOnBlur?: boolean;
  forcePopupIcon?: boolean;
  loading?: boolean;
  noOptionsText?: string;
  className?: string;
  PaperComponent?: React.ComponentType<React.HTMLAttributes<HTMLElement>>;
  disabled?: boolean;
  freeSolo?: boolean;
  blurOnSelect?: boolean;
  onOpen?: () => void;
  onKeyDownCapture?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  ref: React.Ref<HTMLInputElement>;
  disableClearable?: boolean;
}

export interface AutocompleteOption {
  value: string | number;
  label: string;
  id?: string;
  [key: string]: any;
}

export interface MiControlledAutocompleteOptionProps<T = AutocompleteOption> {
  props: React.HTMLAttributes<HTMLLIElement>;
  option: T;
  close: () => void;
}
